import {Component, OnInit, ViewChild} from '@angular/core';
import { ShipmentPreferredAddressesService } from 'src/app/services/shipment-preferred-addresses/shipment-preferred-addresses.service';
import { UserService } from 'src/app/services/user/user.service';
import { User } from 'src/app/services/user/user';
import { NameAndAddress, ShipmentPrefAddress } from 'src/app/shared/common-components/common-structure';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { GridOptions, ColDef, ColGroupDef } from 'ag-grid-community';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { Param, NameAndAddressRecordType } from 'src/app/shared/common-components/AppConstants';
import { NameAndAddressService } from 'src/app/services/name-and-address/name-and-address.service';
import { map, tap } from 'rxjs/operators';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import {ShipmentAddressComponent} from '../shipment-address/shipment-address.component';

@Component({
    selector: 'app-shipment-preferred-addresses',
    templateUrl: './shipment-preferred-addresses.component.html',
    styleUrls: ['./shipment-preferred-addresses.component.css'],
    standalone: false
})
export class ShipmentPreferredAddressesComponent implements OnInit {
  @ViewChild(ShipmentAddressComponent) shipmentAddressComponent: ShipmentAddressComponent
  isPrimaryAddress = false;
  currentUser: User;
  selectedNameAndAddress: NameAndAddress;
  shipmentPreferredAddresses$ = this.shipmentPreferredAddressesService.shipmentPreferredAddresses$;
  shipmentPreferredAddressesGridOptions: GridOptions = this.initializeGridOptions();
  shipmentPreferredAddressForm: UntypedFormGroup;
  addShipmentPreferredAddress$: Observable<any>;
  modifyShipmentPreferredAddress$: Observable<any>;
  deleteShipmentPreferredAddress$: Observable<any>;
  private selectedPreferredAddress = new BehaviorSubject<ShipmentPrefAddress | NameAndAddress>(null);
  selectedPreferredAddress$ = this.selectedPreferredAddress.asObservable();
  isAddressSelected = false;

  vm$ = combineLatest([
    this.selectedPreferredAddress$,
    this.nameAndAddressService.allStates$, this.nameAndAddressService.allCountriesList$])
    .pipe(map(([selectedPreferredAddress, states, countries]) =>
      ({
        selectedPreferredAddress, states, countries
      })));

  constructor(private shipmentPreferredAddressesService: ShipmentPreferredAddressesService,
    private nameAndAddressService: NameAndAddressService, private formBuilder: UntypedFormBuilder,
    public bsModalRef: BsModalRef) {
    this.shipmentPreferredAddressForm = this.formBuilder.group({
      address: []
    });
  }

  ngOnInit() {
    this.selectedPreferredAddress.next(this.selectedNameAndAddress);
    this.updateShipmentPreferredAddressRequest();
    if ((this.selectedNameAndAddress.recordType === NameAndAddressRecordType.ORIGIN ||
      this.selectedNameAndAddress.recordType === NameAndAddressRecordType.DESTINATION)) {
      this.isPrimaryAddress = true;
    }
  }

  addToList() {
    let preferredAddress;
    if ((this.selectedNameAndAddress.recordType === NameAndAddressRecordType.ORIGIN ||
      this.selectedNameAndAddress.recordType === NameAndAddressRecordType.DESTINATION)) {
      preferredAddress =  this.shipmentAddressComponent.addressForm.value as ShipmentPrefAddress;
      preferredAddress.zipCode = this.selectedNameAndAddress.zipCode;
      preferredAddress.state = this.selectedNameAndAddress.stateEntity.stateCountryName;
      preferredAddress.city = this.selectedNameAndAddress.city;
      preferredAddress.countryID = this.selectedNameAndAddress.countryId;
      preferredAddress.countryId = this.selectedNameAndAddress.countryId;
      preferredAddress.defaultAddress = preferredAddress.defaultAddress ? 1 : 0;
    } else {
      preferredAddress = { ...this.shipmentPreferredAddressForm.getRawValue().address } as ShipmentPrefAddress;
    }
    preferredAddress.userId = this.currentUser.id;
    preferredAddress.orgId = this.currentUser.organizationId;
    preferredAddress.shipmentPrefAddressesId = null;
    this.addShipmentPreferredAddress$ = this.shipmentPreferredAddressesService.saveShipmentPrefAddressesById(preferredAddress)
      .pipe(tap(() => this.updateShipmentPreferredAddressRequest()));
  }

  modify() {
    const preferredAddress = { ...this.shipmentPreferredAddressForm.getRawValue().address } as ShipmentPrefAddress;
    preferredAddress.userId = this.currentUser.id;
    preferredAddress.orgId = this.currentUser.organizationId;
    preferredAddress.defaultAddress = preferredAddress.defaultAddress ? 1 : 0;
    this.modifyShipmentPreferredAddress$ = this.shipmentPreferredAddressesService.saveShipmentPrefAddressesById(preferredAddress)
      .pipe(tap(() => this.updateShipmentPreferredAddressRequest()));
  }

  delete() {
    const preferredAddress = { ...this.shipmentPreferredAddressForm.getRawValue().address } as ShipmentPrefAddress;
    this.deleteShipmentPreferredAddress$ = this.shipmentPreferredAddressesService.deleteShipmentPrefAddressesById(preferredAddress.shipmentPrefAddressesId)
      .pipe(tap(() => this.updateShipmentPreferredAddressRequest()));
  }

  selectAddress() {
    this.bsModalRef.hide();
    this.bsModalRef.content.callBackFn({ ...this.selectedPreferredAddress.value });
  }

  private updateShipmentPreferredAddressRequest() {
    let zipCode;
    if ((this.selectedNameAndAddress.recordType === NameAndAddressRecordType.ORIGIN ||
      this.selectedNameAndAddress.recordType === NameAndAddressRecordType.DESTINATION)) {
      zipCode = this.selectedNameAndAddress.zipCode;
    }
    this.shipmentPreferredAddressesService.updateShipmentPreferredAddressRequest(this.currentUser.id, this.currentUser.organizationId,
      this.selectedNameAndAddress.recordType, zipCode);
  }

  private initializeGridOptions() {
    return <GridOptions>{
      columnDefs: this.getShipmentPreferredAddressColumnDefs(),
      rowSelection: 'single',
      defaultColDef: {
        sortable: true,
        resizable: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          suppressAndOrCondition: true
        }
      },
      pagination: true,
      paginationPageSize: 10,
      cacheBlockSize: 10,
      domLayout: 'autoHeight',
      suppressDragLeaveHidesColumns: true,
      onGridReady: params => {
        params.api.sizeColumnsToFit();
      },
      onRowDataChanged: event => {
        this.isAddressSelected = false;
      },
      onSelectionChanged: event => {
        this.isAddressSelected = true;
        this.selectedPreferredAddress.next({...new ShipmentPrefAddress(), ...event.api.getSelectedRows()[0] });
      },
      overlayLoadingTemplate:
        '<span class="ag-overlay-loading-center">Please wait while your rows are loading</span>',
      overlayNoRowsTemplate:
        '<span>No results found. Please adjust your filters.</span>'
    };
  }

  private getShipmentPreferredAddressColumnDefs(): (ColDef | ColGroupDef)[] {
    return [
      <ColDef>
      {
        headerName: 'Line 1',
        field: 'addressLine1'
      },
      {
        headerName: 'Line 2',
        field: 'addressLine2'
      },
      {
        headerName: 'City',
        field: 'city',
        valueFormatter: (params) => params.data.city ?  params.data.city : ''
      },
      {
        headerName: 'State',
        field: 'state',
        valueFormatter: (params) => params.data.state ?  params.data.state : ''
      },
      {
        headerName: 'Zip',
        field: 'zipCode',
        valueFormatter: (params) => params.data.zipCode ?  params.data.zipCode : ''
      },
      {
        headerName: 'Country',
        field: 'country'
      },
      {
        headerName: 'Phone & Ext',
        field: 'phone',
        valueFormatter: (params) => params.data.phoneExt ?
          params.data.phone + ' & ' + params.data.phoneExt : params.data.phone
      },
      {
        headerName: 'Email Address',
        field: 'emailAddress'
      }
    ];
  }
}
